import type { AppState } from "@ch/redux-store/root-reducer";
import { updateAppUserState } from "@ch/redux-store/user/actions";
import type { IAppUserState } from "@ch/redux-store/user/reducer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { KemLocationPermissionModalVariant } from "../components/KemLocationPermissionModal";

const LOCATION_REFETCH_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 10000,
};

export interface UseKemLocationReturn {
  userLocation: IAppUserState["location"];
  isLocationPermissionModalOpen: boolean;
  locationPermissionModalVariant: KemLocationPermissionModalVariant;
  onCloseLocationPermissionModal: () => void;
  onConfirmLocationPermissionModal: () => void;
}

const useKemLocation = (): UseKemLocationReturn => {
  const dispatch = useDispatch();
  const userLocation = useSelector((s: AppState) => s.user.location);
  const locationFetchedAt = useSelector(
    (s: AppState) => s.user.locationFetchedAt,
  );
  const locationPromptDismissedAt = useSelector(
    (s: AppState) => s.user.locationPromptDismissedAt,
  );
  const [isLocationPermissionModalOpen, setIsLocationPermissionModalOpen] =
    useState(false);
  const [locationPermissionModalVariant, setLocationPermissionModalVariant] =
    useState<KemLocationPermissionModalVariant>("prompt");
  const hasPromptedThisSessionRef = useRef(false);

  const needsAutomaticFetch = useMemo(() => {
    if (!userLocation) {
      if (locationPromptDismissedAt) {
        const elapsedDismissed =
          Date.now() - new Date(locationPromptDismissedAt).getTime();
        if (
          Number.isFinite(elapsedDismissed) &&
          elapsedDismissed < LOCATION_REFETCH_INTERVAL_MS
        ) {
          return false;
        }
      }
      return true;
    }
    if (!locationFetchedAt) {
      return true;
    }
    const elapsed = Date.now() - new Date(locationFetchedAt).getTime();
    if (!Number.isFinite(elapsed)) {
      return true;
    }
    return elapsed > LOCATION_REFETCH_INTERVAL_MS;
  }, [userLocation, locationFetchedAt, locationPromptDismissedAt]);

  const saveLocation = useCallback(
    (position: GeolocationPosition) => {
      const { longitude, latitude } = position.coords;

      dispatch(
        updateAppUserState({
          location: { longitude, latitude },
          locationFetchedAt: new Date().toISOString(),
        }),
      );
    },
    [dispatch],
  );

  const requestCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      saveLocation,
      () => {
        // Geolocation errors are expected when permission is denied.
      },
      GEO_OPTIONS,
    );
  }, [saveLocation]);

  const openLocationPermissionModal = useCallback(
    (variant: KemLocationPermissionModalVariant) => {
      setLocationPermissionModalVariant(variant);
      setIsLocationPermissionModalOpen(true);
      hasPromptedThisSessionRef.current = true;
    },
    [],
  );

  const onCloseLocationPermissionModal = useCallback(() => {
    setIsLocationPermissionModalOpen(false);
    hasPromptedThisSessionRef.current = true;
    dispatch(
      updateAppUserState({
        locationPromptDismissedAt: new Date().toISOString(),
      }),
    );
  }, [dispatch]);

  const onConfirmLocationPermissionModal = useCallback(() => {
    setIsLocationPermissionModalOpen(false);
    hasPromptedThisSessionRef.current = true;
    dispatch(
      updateAppUserState({
        locationPromptDismissedAt: new Date().toISOString(),
      }),
    );

    if (locationPermissionModalVariant === "prompt") {
      requestCurrentPosition();
    }
  }, [dispatch, locationPermissionModalVariant, requestCurrentPosition]);

  const tryAutomaticLocationFetch = useCallback(async () => {
    if (!needsAutomaticFetch || hasPromptedThisSessionRef.current) {
      return;
    }

    if (!navigator.geolocation) {
      return;
    }

    try {
      if (navigator.permissions?.query) {
        const result = await navigator.permissions.query({
          name: "geolocation",
        });

        if (result.state === "granted") {
          requestCurrentPosition();
          hasPromptedThisSessionRef.current = true;
          return;
        }

        if (result.state === "denied") {
          openLocationPermissionModal("denied");
          return;
        }
      }
    } catch {
      // Permissions API unavailable; fall through to the pre-prompt modal.
    }

    openLocationPermissionModal("prompt");
  }, [
    needsAutomaticFetch,
    openLocationPermissionModal,
    requestCurrentPosition,
  ]);

  useEffect(() => {
    void tryAutomaticLocationFetch();
  }, [tryAutomaticLocationFetch]);

  return {
    userLocation,
    isLocationPermissionModalOpen,
    locationPermissionModalVariant,
    onCloseLocationPermissionModal,
    onConfirmLocationPermissionModal,
  };
};

export default useKemLocation;
